/**
 * Product Pricing reviews API client — CRUD for saved review criteria plus the
 * live computed results. Mirrors the pure-fetch service pattern (relative
 * /api/* URLs; types from @techgitter/contracts). Admin-gated server-side.
 */

import type {
  PricingReview,
  PricingReviewInput,
  PricingReviewListResponse,
  PricingReviewResults,
} from '@techgitter/contracts';

const BASE = '/api/pricing-reviews';

async function readJson<T>(response: Response, op: string): Promise<T> {
  if (!response.ok) {
    let detail = '';
    try {
      detail = `: ${(await response.text()).slice(0, 200)}`;
    } catch {
      /* ignore */
    }
    throw new Error(`${op} failed (${response.status} ${response.statusText})${detail}`);
  }
  return (await response.json()) as T;
}

export async function fetchReviews(signal?: AbortSignal): Promise<PricingReview[]> {
  const response = await fetch(BASE, { headers: { Accept: 'application/json' }, signal });
  const body = await readJson<PricingReviewListResponse>(response, 'Load pricing reviews');
  return body.items ?? [];
}

export async function createReview(input: PricingReviewInput): Promise<PricingReview> {
  const response = await fetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  return readJson<PricingReview>(response, 'Create pricing review');
}

export async function updateReview(
  id: string,
  input: PricingReviewInput,
): Promise<PricingReview> {
  const response = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  return readJson<PricingReview>(response, 'Update pricing review');
}

export async function deleteReview(id: string): Promise<void> {
  const response = await fetch(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  await readJson<{ deleted: boolean }>(response, 'Delete pricing review');
}

export async function fetchReviewResults(
  id: string,
  signal?: AbortSignal,
): Promise<PricingReviewResults> {
  const response = await fetch(`${BASE}/${encodeURIComponent(id)}/results`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  return readJson<PricingReviewResults>(response, 'Compute pricing review');
}
